/**
 * Output formats, like Adobe Media Encoder: containers, video and audio codecs
 * with their settings, named presets, validation of combinations and file size
 * estimates. Exports and asset conversions both describe their output with an
 * `EncodeSettings`; the worker turns it into ffmpeg arguments
 * (apps/worker/src/encode.ts).
 *
 * Everything here is data plus pure functions so the web app, the module, the
 * API (MCP tools) and the worker agree on what is valid.
 */

export type ContainerKind = 'video' | 'animation' | 'sequence' | 'audio';

export interface ContainerSpec {
  id: string;
  label: string;
  /** Extension of the delivered file (image sequences arrive as a .zip). */
  ext: string;
  mime: string;
  kind: ContainerKind;
  /** ffmpeg muxer (-f). */
  muxer: string;
  videoCodecs: string[];
  audioCodecs: string[];
  /** Can be the target of an asset conversion (the result is media vidmakr can play and edit). */
  convertible: boolean;
  /** Image sequences: extension of each frame inside the zip. */
  frameExt?: string;
  note?: string;
}

export const CONTAINERS: ContainerSpec[] = [
  { id: 'mp4', label: 'MP4', ext: 'mp4', mime: 'video/mp4', kind: 'video', muxer: 'mp4', videoCodecs: ['h264', 'h265', 'av1'], audioCodecs: ['aac', 'mp3', 'opus'], convertible: true },
  { id: 'mov', label: 'QuickTime (MOV)', ext: 'mov', mime: 'video/quicktime', kind: 'video', muxer: 'mov', videoCodecs: ['prores', 'h264', 'h265', 'dnxhr', 'cineform', 'mjpeg'], audioCodecs: ['pcm24', 'pcm16', 'aac'], convertible: true },
  { id: 'mkv', label: 'Matroska (MKV)', ext: 'mkv', mime: 'video/x-matroska', kind: 'video', muxer: 'matroska', videoCodecs: ['h264', 'h265', 'av1', 'vp9', 'prores', 'dnxhr'], audioCodecs: ['aac', 'opus', 'flac', 'pcm24', 'pcm16', 'mp3', 'vorbis'], convertible: true },
  { id: 'webm', label: 'WebM', ext: 'webm', mime: 'video/webm', kind: 'video', muxer: 'webm', videoCodecs: ['vp9', 'av1'], audioCodecs: ['opus', 'vorbis'], convertible: true },
  { id: 'mxf', label: 'MXF OP1a', ext: 'mxf', mime: 'application/mxf', kind: 'video', muxer: 'mxf', videoCodecs: ['dnxhr', 'prores'], audioCodecs: ['pcm24', 'pcm16'], convertible: true, note: 'Broadcast exchange format; audio is always 48 kHz' },
  { id: 'avi', label: 'AVI', ext: 'avi', mime: 'video/x-msvideo', kind: 'video', muxer: 'avi', videoCodecs: ['mjpeg', 'h264'], audioCodecs: ['pcm16', 'mp3'], convertible: true },
  { id: 'gif', label: 'Animated GIF', ext: 'gif', mime: 'image/gif', kind: 'animation', muxer: 'gif', videoCodecs: ['gif'], audioCodecs: [], convertible: true },
  { id: 'webp', label: 'Animated WebP', ext: 'webp', mime: 'image/webp', kind: 'animation', muxer: 'webp', videoCodecs: ['webp'], audioCodecs: [], convertible: false },
  { id: 'png_seq', label: 'PNG sequence (zip)', ext: 'zip', mime: 'application/zip', kind: 'sequence', muxer: 'image2', videoCodecs: ['png'], audioCodecs: [], convertible: false, frameExt: 'png' },
  { id: 'jpeg_seq', label: 'JPEG sequence (zip)', ext: 'zip', mime: 'application/zip', kind: 'sequence', muxer: 'image2', videoCodecs: ['mjpeg'], audioCodecs: [], convertible: false, frameExt: 'jpg' },
  { id: 'tiff_seq', label: 'TIFF sequence (zip)', ext: 'zip', mime: 'application/zip', kind: 'sequence', muxer: 'image2', videoCodecs: ['tiff'], audioCodecs: [], convertible: false, frameExt: 'tif' },
  { id: 'exr_seq', label: 'OpenEXR sequence (zip)', ext: 'zip', mime: 'application/zip', kind: 'sequence', muxer: 'image2', videoCodecs: ['exr'], audioCodecs: [], convertible: false, frameExt: 'exr' },
  { id: 'wav', label: 'WAV', ext: 'wav', mime: 'audio/wav', kind: 'audio', muxer: 'wav', videoCodecs: [], audioCodecs: ['pcm24', 'pcm16'], convertible: true },
  { id: 'aiff', label: 'AIFF', ext: 'aiff', mime: 'audio/aiff', kind: 'audio', muxer: 'aiff', videoCodecs: [], audioCodecs: ['pcm24', 'pcm16'], convertible: true },
  { id: 'mp3', label: 'MP3', ext: 'mp3', mime: 'audio/mpeg', kind: 'audio', muxer: 'mp3', videoCodecs: [], audioCodecs: ['mp3'], convertible: true },
  { id: 'm4a', label: 'AAC (M4A)', ext: 'm4a', mime: 'audio/mp4', kind: 'audio', muxer: 'ipod', videoCodecs: [], audioCodecs: ['aac'], convertible: true },
  { id: 'flac', label: 'FLAC', ext: 'flac', mime: 'audio/flac', kind: 'audio', muxer: 'flac', videoCodecs: [], audioCodecs: ['flac'], convertible: true },
  { id: 'opus', label: 'Opus (Ogg)', ext: 'opus', mime: 'audio/ogg', kind: 'audio', muxer: 'opus', videoCodecs: [], audioCodecs: ['opus'], convertible: true },
];

export type RateControl = 'crf' | 'cbr' | 'vbr';

export const PIXEL_FORMATS = [
  { id: 'yuv420p', label: '8-bit 4:2:0', bits: 8 },
  { id: 'yuv422p', label: '8-bit 4:2:2', bits: 8 },
  { id: 'yuv444p', label: '8-bit 4:4:4', bits: 8 },
  { id: 'yuv420p10le', label: '10-bit 4:2:0', bits: 10 },
  { id: 'yuv422p10le', label: '10-bit 4:2:2', bits: 10 },
  { id: 'yuv444p10le', label: '10-bit 4:4:4', bits: 10 },
  { id: 'rgb24', label: '8-bit RGB', bits: 8 },
  { id: 'rgb48', label: '16-bit RGB', bits: 16 },
  { id: 'gbrpf32le', label: 'Half float RGB', bits: 16 },
  { id: 'pal8', label: '256 colors', bits: 8 },
] as const;

export type PixelFormat = (typeof PIXEL_FORMATS)[number]['id'];

export interface CodecProfile {
  id: string;
  label: string;
  /** Pixel formats this profile allows (default: the codec's). */
  pixelFormats?: PixelFormat[];
  /** Bits per pixel, for codecs whose data rate the profile fixes. */
  bpp?: number;
}

export interface VideoCodecSpec {
  id: string;
  label: string;
  /** CPU encoders in order of preference. */
  encoders: string[];
  /** NVENC encoder, when GPUs can encode it. */
  nvenc?: string;
  /** Allowed pixel formats; the first is the default. */
  pixelFormats: PixelFormat[];
  nvencPixelFormats?: PixelFormat[];
  profiles?: CodecProfile[];
  defaultProfile?: string;
  /** Levels the encoder can be held to ('' = automatic). */
  levels?: string[];
  /** Ways to control the data rate; empty when the profile fixes it. */
  rateControls: RateControl[];
  /** The quality value's range and default (CRF-like: lower is better unless `higherIsBetter`). */
  quality?: { min: number; max: number; default: number; label: string; higherIsBetter?: boolean };
  twoPass?: boolean;
  /** Long-GOP codec: a keyframe interval applies. */
  gop: boolean;
  /** Mastering codec: part of the plans with master formats. */
  master?: boolean;
  /** Size estimate: bits per pixel at quality `ref`, doubling every `doubling` steps towards better. */
  bpp: { ref: number; bpp: number; doubling: number };
  /** Frame size rules. */
  size?: { widthMultipleOf?: number; minWidth?: number; minHeight?: number; maxSide?: number };
}

const H264_LEVELS = ['3.1', '4.0', '4.1', '4.2', '5.0', '5.1', '5.2', '6.0', '6.1', '6.2'];
const HEVC_LEVELS = ['4.0', '4.1', '5.0', '5.1', '5.2', '6.0', '6.1', '6.2'];

export const VIDEO_CODECS: VideoCodecSpec[] = [
  {
    id: 'h264',
    label: 'H.264 / AVC',
    encoders: ['libx264'],
    nvenc: 'h264_nvenc',
    pixelFormats: ['yuv420p', 'yuv422p', 'yuv444p', 'yuv420p10le', 'yuv422p10le'],
    nvencPixelFormats: ['yuv420p', 'yuv444p'],
    profiles: [
      // High 10 / High 4:2:2 / High 4:4:4 follow from the pixel format.
      { id: 'high', label: 'High' },
      { id: 'main', label: 'Main', pixelFormats: ['yuv420p'] },
      { id: 'baseline', label: 'Baseline', pixelFormats: ['yuv420p'] },
    ],
    defaultProfile: 'high',
    levels: H264_LEVELS,
    rateControls: ['crf', 'vbr', 'cbr'],
    quality: { min: 0, max: 51, default: 18, label: 'CRF' },
    twoPass: true,
    gop: true,
    bpp: { ref: 23, bpp: 0.07, doubling: 6 },
  },
  {
    id: 'h265',
    label: 'H.265 / HEVC',
    encoders: ['libx265'],
    nvenc: 'hevc_nvenc',
    pixelFormats: ['yuv420p', 'yuv420p10le', 'yuv422p', 'yuv422p10le', 'yuv444p', 'yuv444p10le'],
    nvencPixelFormats: ['yuv420p', 'yuv420p10le', 'yuv444p'],
    levels: HEVC_LEVELS,
    rateControls: ['crf', 'vbr', 'cbr'],
    quality: { min: 0, max: 51, default: 22, label: 'CRF' },
    twoPass: true,
    gop: true,
    bpp: { ref: 28, bpp: 0.025, doubling: 6 },
  },
  {
    id: 'av1',
    label: 'AV1',
    encoders: ['libsvtav1', 'libaom-av1'],
    nvenc: 'av1_nvenc',
    pixelFormats: ['yuv420p', 'yuv420p10le'],
    nvencPixelFormats: ['yuv420p', 'yuv420p10le'],
    rateControls: ['crf', 'vbr'],
    quality: { min: 0, max: 63, default: 30, label: 'CRF' },
    gop: true,
    bpp: { ref: 35, bpp: 0.02, doubling: 8 },
  },
  {
    id: 'vp9',
    label: 'VP9',
    encoders: ['libvpx-vp9'],
    pixelFormats: ['yuv420p', 'yuv420p10le', 'yuv422p', 'yuv444p'],
    rateControls: ['crf', 'vbr', 'cbr'],
    quality: { min: 0, max: 63, default: 31, label: 'CRF' },
    twoPass: true,
    gop: true,
    bpp: { ref: 31, bpp: 0.045, doubling: 8 },
  },
  {
    id: 'prores',
    label: 'Apple ProRes',
    encoders: ['prores_ks'],
    pixelFormats: ['yuv422p10le', 'yuv444p10le'],
    profiles: [
      { id: 'proxy', label: 'ProRes 422 Proxy', pixelFormats: ['yuv422p10le'], bpp: 0.72 },
      { id: 'lt', label: 'ProRes 422 LT', pixelFormats: ['yuv422p10le'], bpp: 1.64 },
      { id: 'standard', label: 'ProRes 422', pixelFormats: ['yuv422p10le'], bpp: 2.37 },
      { id: 'hq', label: 'ProRes 422 HQ', pixelFormats: ['yuv422p10le'], bpp: 3.54 },
      { id: '4444', label: 'ProRes 4444', pixelFormats: ['yuv444p10le'], bpp: 5.3 },
      { id: '4444xq', label: 'ProRes 4444 XQ', pixelFormats: ['yuv444p10le'], bpp: 8 },
    ],
    defaultProfile: 'hq',
    rateControls: [],
    gop: false,
    master: true,
    bpp: { ref: 0, bpp: 3.54, doubling: 1 },
  },
  {
    id: 'dnxhr',
    label: 'Avid DNxHR',
    encoders: ['dnxhd'],
    pixelFormats: ['yuv422p', 'yuv422p10le', 'yuv444p10le'],
    profiles: [
      { id: 'lb', label: 'DNxHR LB', pixelFormats: ['yuv422p'], bpp: 0.72 },
      { id: 'sq', label: 'DNxHR SQ', pixelFormats: ['yuv422p'], bpp: 2.33 },
      { id: 'hq', label: 'DNxHR HQ', pixelFormats: ['yuv422p'], bpp: 3.54 },
      { id: 'hqx', label: 'DNxHR HQX', pixelFormats: ['yuv422p10le'], bpp: 3.54 },
      { id: '444', label: 'DNxHR 444', pixelFormats: ['yuv444p10le'], bpp: 7.07 },
    ],
    defaultProfile: 'hq',
    rateControls: [],
    gop: false,
    master: true,
    bpp: { ref: 0, bpp: 3.54, doubling: 1 },
    size: { minWidth: 256, minHeight: 120 },
  },
  {
    id: 'cineform',
    label: 'GoPro CineForm',
    encoders: ['cfhd'],
    pixelFormats: ['yuv422p10le'],
    profiles: [
      { id: 'low', label: 'CineForm Low', bpp: 0.8 },
      { id: 'medium', label: 'CineForm Medium', bpp: 1.2 },
      { id: 'high', label: 'CineForm High', bpp: 1.8 },
      { id: 'film1', label: 'CineForm Film Scan 1', bpp: 2.5 },
      { id: 'film2', label: 'CineForm Film Scan 2', bpp: 3.5 },
      { id: 'film3+', label: 'CineForm Film Scan 3+', bpp: 5 },
    ],
    defaultProfile: 'film1',
    rateControls: [],
    gop: false,
    master: true,
    bpp: { ref: 0, bpp: 2.5, doubling: 1 },
    size: { widthMultipleOf: 16 },
  },
  {
    id: 'mjpeg',
    label: 'JPEG / Motion JPEG',
    encoders: ['mjpeg'],
    pixelFormats: ['yuv420p', 'yuv422p', 'yuv444p'],
    rateControls: ['crf'],
    quality: { min: 2, max: 31, default: 3, label: 'Quantizer (q)' },
    gop: false,
    bpp: { ref: 3, bpp: 2, doubling: 3 },
  },
  {
    id: 'gif',
    label: 'GIF',
    encoders: ['gif'],
    pixelFormats: ['pal8'],
    rateControls: [],
    gop: false,
    bpp: { ref: 0, bpp: 1, doubling: 1 },
    size: { maxSide: 2048 },
  },
  {
    id: 'webp',
    label: 'WebP',
    encoders: ['libwebp_anim'],
    pixelFormats: ['yuv420p'],
    rateControls: ['crf'],
    quality: { min: 0, max: 100, default: 75, label: 'Quality', higherIsBetter: true },
    gop: false,
    bpp: { ref: 75, bpp: 0.15, doubling: 20 },
    size: { maxSide: 4096 },
  },
  { id: 'png', label: 'PNG', encoders: ['png'], pixelFormats: ['rgb24', 'rgb48'], rateControls: [], gop: false, master: true, bpp: { ref: 0, bpp: 12, doubling: 1 } },
  { id: 'tiff', label: 'TIFF', encoders: ['tiff'], pixelFormats: ['rgb48', 'rgb24'], rateControls: [], gop: false, master: true, bpp: { ref: 0, bpp: 14, doubling: 1 } },
  { id: 'exr', label: 'OpenEXR (half float)', encoders: ['exr'], pixelFormats: ['gbrpf32le'], rateControls: [], gop: false, master: true, bpp: { ref: 0, bpp: 30, doubling: 1 } },
];

export interface AudioCodecSpec {
  id: string;
  label: string;
  /** Lossy codecs take a bitrate (kbps); lossless ones do not. */
  bitrates: number[];
  defaultBitrate: number;
  sampleRates: number[];
  /** Bits per sample of uncompressed codecs (for size estimates). */
  bits?: number;
}

export const AUDIO_CODECS: AudioCodecSpec[] = [
  { id: 'aac', label: 'AAC', bitrates: [96, 128, 160, 192, 256, 320], defaultBitrate: 256, sampleRates: [44100, 48000, 96000] },
  { id: 'mp3', label: 'MP3', bitrates: [64, 96, 128, 160, 192, 256, 320], defaultBitrate: 192, sampleRates: [44100, 48000] },
  { id: 'opus', label: 'Opus', bitrates: [48, 64, 96, 128, 160, 192, 256], defaultBitrate: 160, sampleRates: [48000] },
  { id: 'vorbis', label: 'Vorbis', bitrates: [96, 128, 160, 192, 256, 320], defaultBitrate: 192, sampleRates: [44100, 48000] },
  { id: 'flac', label: 'FLAC (lossless)', bitrates: [], defaultBitrate: 0, sampleRates: [44100, 48000, 96000], bits: 10 },
  { id: 'pcm16', label: 'PCM 16-bit (uncompressed)', bitrates: [], defaultBitrate: 0, sampleRates: [44100, 48000, 96000], bits: 16 },
  { id: 'pcm24', label: 'PCM 24-bit (uncompressed)', bitrates: [], defaultBitrate: 0, sampleRates: [44100, 48000, 96000], bits: 24 },
];

export const COLOR_SPACES = [
  { id: 'auto', label: 'Not tagged' },
  { id: 'bt709', label: 'Rec. 709' },
  { id: 'bt601', label: 'Rec. 601' },
  { id: 'bt2020', label: 'Rec. 2020' },
] as const;

export const ENCODER_SPEEDS = [
  { id: 'fast', label: 'Fast' },
  { id: 'balanced', label: 'Balanced' },
  { id: 'quality', label: 'Best compression (slow)' },
] as const;

export const SCALE_MODES = [
  { id: 'fit', label: 'Fit (black bars)' },
  { id: 'fill', label: 'Fill (crop)' },
  { id: 'stretch', label: 'Stretch' },
] as const;

/** Frame rates exports and conversions can use (fractional NTSC rates included). */
export const ENCODE_FPS = [12, 15, 23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60, 120] as const;

/** Every setting of an output, codec values in the codec's own terms. */
export interface EncodeSettings {
  container: string;
  /** '' for audio-only containers. */
  videoCodec: string;
  /** Codec profile id (ProRes flavor, DNxHR class, H.264 profile); '' = codec default. */
  profile: string;
  /** '' = automatic. */
  level: string;
  /** '' = the codec's (or profile's) default. */
  pixelFormat: string;
  rateControl: RateControl;
  /** Constant quality, in the codec's scale (see VideoCodecSpec.quality). */
  quality: number;
  /** Target bitrate for CBR/VBR (kbps). */
  bitrateKbps: number;
  /** Peak bitrate for VBR (kbps). */
  maxBitrateKbps: number;
  twoPass: boolean;
  /** Frames between keyframes; 0 = encoder default. */
  keyframeInterval: number;
  colorSpace: string;
  speed: string;
  /** How the picture fits a frame of another aspect ratio. */
  scaleMode: string;
  /** '' = no audio. */
  audioCodec: string;
  audioBitrateKbps: number;
  /** 0 = 48 kHz for exports, the source's for conversions. */
  audioSampleRate: number;
  /** 0 = stereo for exports, the source's for conversions. */
  audioChannels: number;
  /** Animations (GIF, WebP): play once, or forwards then backwards; absent = loop forever. */
  animLoop?: AnimLoop;
  /** Keep only this part of the picture, scaled to its own output size (see EncodeCrop). */
  crop?: EncodeCrop;
}

export type AnimLoop = 'forever' | 'once' | 'bounce';
export const ANIM_LOOPS: { id: AnimLoop; label: string }[] = [
  { id: 'forever', label: 'Loop forever' },
  { id: 'once', label: 'Play once' },
  { id: 'bounce', label: 'Back and forth' },
];

/**
 * A crop of the picture: the part kept, as fractions of the frame (x, y from
 * the top left; w, h its size), and the output size it is scaled to in pixels.
 */
export interface EncodeCrop {
  x: number;
  y: number;
  w: number;
  h: number;
  width: number;
  height: number;
}

/** A crop that is valid and keeps less than the whole frame, else undefined. */
export function normalizeCrop(c: unknown): EncodeCrop | undefined {
  if (!c || typeof c !== 'object') return undefined;
  const r = c as Record<string, unknown>;
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : NaN);
  const x = Math.max(0, Math.min(1, n(r.x)));
  const y = Math.max(0, Math.min(1, n(r.y)));
  const w = Math.min(1 - x, n(r.w));
  const h = Math.min(1 - y, n(r.h));
  const width = Math.round(n(r.width));
  const height = Math.round(n(r.height));
  if (![x, y, w, h, width, height].every(Number.isFinite) || w <= 0.01 || h <= 0.01 || width <= 0 || height <= 0) return undefined;
  return { x, y, w, h, width, height };
}

export const DEFAULT_ENCODE: EncodeSettings = {
  container: 'mp4',
  videoCodec: 'h264',
  profile: '',
  level: '',
  pixelFormat: '',
  rateControl: 'crf',
  quality: 18,
  bitrateKbps: 0,
  maxBitrateKbps: 0,
  twoPass: false,
  keyframeInterval: 0,
  colorSpace: 'auto',
  speed: 'balanced',
  scaleMode: 'fit',
  audioCodec: 'aac',
  audioBitrateKbps: 320,
  audioSampleRate: 48000,
  audioChannels: 2,
};

export const containerSpec = (id: string) => CONTAINERS.find((c) => c.id === id);
export const videoCodecSpec = (id: string) => VIDEO_CODECS.find((c) => c.id === id);
export const audioCodecSpec = (id: string) => AUDIO_CODECS.find((c) => c.id === id);

/** The profile in effect (the codec default when none is set). */
export function profileOf(e: Pick<EncodeSettings, 'videoCodec' | 'profile'>): CodecProfile | undefined {
  const codec = videoCodecSpec(e.videoCodec);
  const id = e.profile || codec?.defaultProfile;
  return codec?.profiles?.find((p) => p.id === id);
}

/** Pixel formats allowed for the codec and profile (on NVENC: what the GPU can encode). */
export function pixelFormatsFor(e: Pick<EncodeSettings, 'videoCodec' | 'profile'>, gpu = false): PixelFormat[] {
  const codec = videoCodecSpec(e.videoCodec);
  if (!codec) return [];
  const base = profileOf(e)?.pixelFormats ?? codec.pixelFormats;
  return gpu && codec.nvencPixelFormats ? base.filter((p) => codec.nvencPixelFormats!.includes(p)) : base;
}

/** The pixel format in effect. */
export function pixelFormatOf(e: Pick<EncodeSettings, 'videoCodec' | 'profile' | 'pixelFormat'>): string {
  return e.pixelFormat || pixelFormatsFor(e)[0] || '';
}

const isVideoContainer = (c: ContainerSpec | undefined) => !!c && c.kind !== 'audio';

/** Whether the output has a picture / a sound track. */
export const encodeHasVideo = (e: EncodeSettings) => isVideoContainer(containerSpec(e.container)) && !!e.videoCodec;
export const encodeHasAudio = (e: EncodeSettings) => !!e.audioCodec && (containerSpec(e.container)?.audioCodecs.length ?? 0) > 0;

/** Whether NVENC GPUs can encode these settings. */
export function nvencSupports(e: EncodeSettings): boolean {
  const codec = videoCodecSpec(e.videoCodec);
  return !!codec?.nvenc && encodeHasVideo(e) && pixelFormatsFor(e, true).includes(pixelFormatOf(e) as PixelFormat);
}

/** Mastering codecs (ProRes, DNxHR, CineForm, image sequences) are part of the plans with master formats. */
export const isMasterEncode = (e: EncodeSettings) => encodeHasVideo(e) && !!videoCodecSpec(e.videoCodec)?.master;

/**
 * Fills in what is missing and keeps every setting consistent with the ones
 * before it: a codec the container cannot hold becomes the container's first,
 * a profile or pixel format the codec does not have becomes its default, and
 * so on. Use it after changing one setting (`adaptEncode(old, { container })`).
 */
export function adaptEncode(prev: Partial<EncodeSettings>, patch: Partial<EncodeSettings> = {}): EncodeSettings {
  const e: EncodeSettings = { ...DEFAULT_ENCODE, ...stripUndefined(prev), ...stripUndefined(patch) };
  const container = containerSpec(e.container) ?? containerSpec('mp4')!;
  e.container = container.id;
  const codecChanged = patch.videoCodec !== undefined && patch.videoCodec !== prev.videoCodec;
  if (!container.videoCodecs.includes(e.videoCodec)) {
    e.videoCodec = container.videoCodecs[0] ?? '';
  }
  const codec = videoCodecSpec(e.videoCodec);
  if (!codec) {
    Object.assign(e, { profile: '', level: '', pixelFormat: '', keyframeInterval: 0 });
  } else {
    if (e.profile && !codec.profiles?.some((p) => p.id === e.profile)) e.profile = '';
    if (e.level && !codec.levels?.includes(e.level)) e.level = '';
    if (e.pixelFormat && !pixelFormatsFor(e).includes(e.pixelFormat as PixelFormat)) e.pixelFormat = '';
    if (!codec.rateControls.length) e.rateControl = 'crf';
    else if (!codec.rateControls.includes(e.rateControl)) e.rateControl = codec.rateControls[0];
    const q = codec.quality;
    if (q && (codecChanged || prev.videoCodec !== e.videoCodec || !(e.quality >= q.min && e.quality <= q.max))) e.quality = q.default;
    if (e.rateControl !== 'crf' && !(e.bitrateKbps > 0)) e.bitrateKbps = defaultBitrateKbps(e.videoCodec);
    if (e.rateControl === 'vbr' && !(e.maxBitrateKbps >= e.bitrateKbps)) e.maxBitrateKbps = Math.round(e.bitrateKbps * 1.5);
    if (e.rateControl !== 'vbr' || !codec.twoPass) e.twoPass = false;
    if (!codec.gop) e.keyframeInterval = 0;
  }
  if (!container.audioCodecs.length) e.audioCodec = '';
  else if (e.audioCodec && !container.audioCodecs.includes(e.audioCodec)) e.audioCodec = container.audioCodecs[0];
  else if (!e.audioCodec && patch.container !== undefined && patch.container !== prev.container) e.audioCodec = container.audioCodecs[0];
  const audio = audioCodecSpec(e.audioCodec);
  if (audio) {
    if (audio.bitrates.length && !audio.bitrates.includes(e.audioBitrateKbps)) {
      e.audioBitrateKbps = audio.bitrates.reduce((best, b) => (Math.abs(b - e.audioBitrateKbps) < Math.abs(best - e.audioBitrateKbps) ? b : best), audio.defaultBitrate);
    }
    if (!audio.bitrates.length) e.audioBitrateKbps = 0;
    if (e.audioSampleRate && !audio.sampleRates.includes(e.audioSampleRate)) e.audioSampleRate = audio.sampleRates.includes(48000) ? 48000 : audio.sampleRates[0];
  }
  if (container.id === 'mxf' && e.audioSampleRate && e.audioSampleRate !== 48000) e.audioSampleRate = 48000;
  return e;
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

/** A sensible bitrate (kbps) for CBR/VBR at 1080p. */
export function defaultBitrateKbps(codec: string): number {
  return codec === 'h265' ? 8000 : codec === 'av1' ? 6000 : codec === 'vp9' ? 8000 : 12000;
}

/**
 * Settings as they will be used: missing fields filled in. Unlike `adaptEncode`
 * an explicit but invalid choice is kept so `validateEncode` can report it.
 */
export function normalizeEncode(raw: Partial<EncodeSettings>): EncodeSettings {
  const container = containerSpec(String(raw.container ?? DEFAULT_ENCODE.container));
  const base: Partial<EncodeSettings> = { container: container?.id ?? String(raw.container) };
  if (container) {
    base.videoCodec = container.videoCodecs[0] ?? '';
    base.audioCodec = container.audioCodecs[0] ?? '';
  }
  const codec = videoCodecSpec(String(raw.videoCodec ?? base.videoCodec ?? ''));
  const e: EncodeSettings = {
    ...DEFAULT_ENCODE,
    ...base,
    ...(codec?.quality ? { quality: codec.quality.default } : {}),
    ...(codec && codec.rateControls.length ? { rateControl: codec.rateControls[0] } : {}),
    ...stripUndefined(raw),
  };
  const audio = audioCodecSpec(e.audioCodec);
  if (raw.audioBitrateKbps === undefined && audio) e.audioBitrateKbps = audio.defaultBitrate;
  if (raw.maxBitrateKbps === undefined && e.rateControl === 'vbr') e.maxBitrateKbps = Math.round(e.bitrateKbps * 1.5);
  for (const k of ['quality', 'bitrateKbps', 'maxBitrateKbps', 'keyframeInterval', 'audioBitrateKbps', 'audioSampleRate', 'audioChannels'] as const) {
    e[k] = Number(e[k]) || 0;
  }
  for (const k of ['container', 'videoCodec', 'profile', 'level', 'pixelFormat', 'colorSpace', 'speed', 'scaleMode', 'audioCodec'] as const) {
    e[k] = String(e[k] ?? '');
  }
  e.twoPass = !!e.twoPass;
  if (e.animLoop !== 'once' && e.animLoop !== 'bounce') delete e.animLoop;
  const crop = normalizeCrop(e.crop);
  if (crop) e.crop = crop;
  else delete e.crop;
  return e;
}

export interface EncodeTarget {
  width: number;
  height: number;
  fps: number;
  /** Rendered or converted on a GPU worker (NVENC). */
  gpu?: boolean;
}

/** The first problem with a combination of settings, or null when it can be encoded. */
export function validateEncode(e: EncodeSettings, t: EncodeTarget): string | null {
  const container = containerSpec(e.container);
  if (!container) return `Unknown format "${e.container}"`;
  const hasVideo = container.kind !== 'audio';
  if (hasVideo) {
    const codec = videoCodecSpec(e.videoCodec);
    if (!codec) return `Unknown video codec "${e.videoCodec}"`;
    if (!container.videoCodecs.includes(codec.id)) return `${container.label} cannot hold ${codec.label} video`;
    if (e.profile && !codec.profiles?.some((p) => p.id === e.profile)) return `${codec.label} has no profile "${e.profile}"`;
    if (e.level && !codec.levels?.includes(e.level)) return `${codec.label} has no level "${e.level}"`;
    const pix = pixelFormatOf(e);
    if (!pixelFormatsFor(e).includes(pix as PixelFormat)) {
      return `${profileOf(e)?.label ?? codec.label} cannot be encoded as ${PIXEL_FORMATS.find((p) => p.id === pix)?.label ?? pix}`;
    }
    if (codec.rateControls.length) {
      if (!codec.rateControls.includes(e.rateControl)) return `${codec.label} does not support ${e.rateControl.toUpperCase()} encoding`;
      if (e.rateControl === 'crf' && codec.quality && !(e.quality >= codec.quality.min && e.quality <= codec.quality.max)) {
        return `${codec.quality.label} must be between ${codec.quality.min} and ${codec.quality.max}`;
      }
      if (e.rateControl !== 'crf') {
        if (!(e.bitrateKbps >= 100 && e.bitrateKbps <= 800_000)) return 'Bitrate must be between 0.1 and 800 Mbps';
        if (e.rateControl === 'vbr' && e.maxBitrateKbps && e.maxBitrateKbps < e.bitrateKbps) return 'Maximum bitrate must be at least the target bitrate';
      }
    }
    if (e.twoPass && !(e.rateControl === 'vbr' && (codec.twoPass || (t.gpu && codec.nvenc)))) return '2-pass encoding needs VBR with H.264, H.265 or VP9';
    if (!(e.keyframeInterval >= 0 && e.keyframeInterval <= 1000)) return 'Keyframe interval must be between 0 and 1000 frames';
    if (!COLOR_SPACES.some((c) => c.id === e.colorSpace)) return `Unknown color space "${e.colorSpace}"`;
    if (!ENCODER_SPEEDS.some((s) => s.id === e.speed)) return `Unknown encoder speed "${e.speed}"`;
    if (!SCALE_MODES.some((s) => s.id === e.scaleMode)) return `Unknown scale mode "${e.scaleMode}"`;
    const { width: w, height: h } = t;
    if (w > 0 && h > 0) {
      const chroma = pix.startsWith('yuv420') || pix.startsWith('yuv422');
      if (chroma && (w % 2 || h % 2)) return 'Width and height must be even';
      if (codec.size?.widthMultipleOf && w % codec.size.widthMultipleOf) return `${codec.label} needs a width that is a multiple of ${codec.size.widthMultipleOf}`;
      if (codec.size?.minWidth && (w < codec.size.minWidth || h < (codec.size.minHeight ?? 0))) {
        return `${codec.label} needs at least ${codec.size.minWidth}×${codec.size.minHeight ?? 0}`;
      }
      if (codec.size?.maxSide && Math.max(w, h) > codec.size.maxSide) return `${codec.label} can be at most ${codec.size.maxSide} pixels wide or high`;
    }
    if (e.animLoop && container.id !== 'gif' && container.id !== 'webp') return 'Only GIF and WebP animations can loop';
    if (e.crop) {
      const c = e.crop;
      if (c.width < 16 || c.height < 16 || c.width > 7680 || c.height > 7680) return 'The cropped size must be between 16 and 7680 pixels';
      if (c.width % 2 || c.height % 2) return 'Width and height must be even';
    }
    if (t.fps > 0 && container.id === 'mxf' && !MXF_FPS.some((f) => Math.abs(f - t.fps) < 0.01)) return `MXF needs a standard frame rate (${MXF_FPS.join(', ')})`;
    if (t.gpu && !nvencSupports(e)) {
      return codec.nvenc
        ? `GPU workers cannot encode ${codec.label} as ${PIXEL_FORMATS.find((p) => p.id === pix)?.label ?? pix}`
        : `GPU workers encode H.264, H.265 and AV1 only; render ${codec.label} on the cloud CPU workers`;
    }
  } else if (e.videoCodec) return `${container.label} is audio only`;
  if (e.audioCodec) {
    const audio = audioCodecSpec(e.audioCodec);
    if (!audio) return `Unknown audio codec "${e.audioCodec}"`;
    if (!container.audioCodecs.includes(audio.id)) return `${container.label} cannot hold ${audio.label} audio`;
    if (audio.bitrates.length && !(e.audioBitrateKbps >= 32 && e.audioBitrateKbps <= 512)) return 'Audio bitrate must be between 32 and 512 kbps';
    if (e.audioSampleRate && !audio.sampleRates.includes(e.audioSampleRate)) return `${audio.label} supports ${audio.sampleRates.map((r) => `${r / 1000} kHz`).join(', ')}`;
    if (container.id === 'mxf' && e.audioSampleRate && e.audioSampleRate !== 48000) return 'MXF audio must be 48 kHz';
    if (![0, 1, 2].includes(e.audioChannels)) return 'Audio must be mono or stereo';
  } else if (container.kind === 'audio') return `Choose an audio codec for ${container.label}`;
  return null;
}

const MXF_FPS = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];

/** Short id of the format, kept in a job's `format` for listings (e.g. `mov_prores`, `wav`). */
export function formatIdOf(e: EncodeSettings): string {
  return encodeHasVideo(e) && containerSpec(e.container)?.kind === 'video' ? `${e.container}_${e.videoCodec}` : e.container;
}

/** Codec name for a file name or a listing: "ProRes 422 HQ", "H.264", "MP3 192 kbps". */
export function codecLabel(e: EncodeSettings): string {
  const container = containerSpec(e.container);
  if (encodeHasVideo(e)) {
    const profile = profileOf(e);
    if (profile && videoCodecSpec(e.videoCodec)?.rateControls.length === 0) return profile.label;
    if (container?.kind !== 'video') return container?.label.replace(/ \(zip\)$/, '') ?? e.container;
    const label = videoCodecSpec(e.videoCodec)?.label.split(' / ')[0] ?? e.videoCodec;
    return pixelFormatOf(e).includes('10le') ? `${label} 10-bit` : label;
  }
  const audio = audioCodecSpec(e.audioCodec);
  if (!audio) return container?.label ?? e.container;
  return audio.bitrates.length ? `${audio.label} ${e.audioBitrateKbps} kbps` : (container?.label ?? audio.label);
}

/** One line for listings: "MOV · ProRes 422 HQ · PCM 24-bit". */
export function describeEncode(e: EncodeSettings): string {
  const container = containerSpec(e.container);
  const parts = [container?.label ?? e.container];
  if (encodeHasVideo(e) && container?.kind === 'video') {
    parts.push(codecLabel(e));
    const codec = videoCodecSpec(e.videoCodec);
    if (codec?.rateControls.length) {
      parts.push(e.rateControl === 'crf' ? `${codec.quality?.label ?? 'Q'} ${e.quality}` : `${e.rateControl.toUpperCase()} ${(e.bitrateKbps / 1000).toFixed(e.bitrateKbps % 1000 ? 1 : 0)} Mbps${e.twoPass ? ' 2-pass' : ''}`);
    }
  }
  const audio = audioCodecSpec(e.audioCodec);
  if (audio && encodeHasAudio(e)) parts.push(audio.bitrates.length ? `${audio.label} ${e.audioBitrateKbps}k` : audio.label.replace(/ \(.*\)$/, ''));
  return parts.join(' · ');
}

/** Bits per pixel the video will use (rough). */
function videoBpp(e: EncodeSettings): number {
  const codec = videoCodecSpec(e.videoCodec);
  if (!codec) return 0;
  const profile = profileOf(e);
  if (profile?.bpp) return profile.bpp;
  const depth = PIXEL_FORMATS.find((p) => p.id === pixelFormatOf(e))?.bits ?? 8;
  let bpp = codec.bpp.bpp;
  if (codec.quality && e.rateControl === 'crf') {
    const steps = codec.quality.higherIsBetter ? e.quality - codec.bpp.ref : codec.bpp.ref - e.quality;
    bpp *= 2 ** (steps / codec.bpp.doubling);
  }
  // Image formats store more bits per channel; long-GOP codecs grow a little with depth and chroma.
  if (codec.id === 'png' || codec.id === 'tiff') return bpp * (depth / 8);
  return bpp * (codec.gop ? 1 + (depth - 8) * 0.05 : 1);
}

/** Rough size of an output (bytes). */
export function estimateEncodeBytes(e: EncodeSettings, t: { width: number; height: number; fps: number; durationUs: number }): number {
  const secs = Math.max(0, t.durationUs) / 1e6;
  let bps = 0;
  if (encodeHasVideo(e)) {
    const codec = videoCodecSpec(e.videoCodec);
    bps += codec?.rateControls.length && e.rateControl !== 'crf' ? e.bitrateKbps * 1000 : t.width * t.height * Math.max(1, t.fps) * videoBpp(e);
  }
  if (encodeHasAudio(e)) {
    const audio = audioCodecSpec(e.audioCodec);
    const rate = e.audioSampleRate || 48000;
    const channels = e.audioChannels || 2;
    bps += audio?.bitrates.length ? e.audioBitrateKbps * 1000 : rate * channels * (audio?.bits ?? 16);
  }
  return (bps / 8) * secs;
}

/** The legacy export formats (`format` + `quality`, before EncodeSettings) as settings. */
export function legacyEncode(format: string, quality = 'high'): EncodeSettings {
  const q = (low: number, medium: number, high: number, lossless: number) =>
    quality === 'low' ? low : quality === 'medium' ? medium : quality === 'lossless' ? lossless : high;
  switch (format) {
    case 'mp4_h265':
      return adaptEncode({ container: 'mp4', videoCodec: 'h265', quality: q(30, 26, 21, 12), audioCodec: 'aac', audioBitrateKbps: quality === 'low' ? 128 : 256 });
    case 'mov_prores':
      return adaptEncode({ container: 'mov', videoCodec: 'prores', profile: quality === 'low' ? 'lt' : quality === 'medium' ? 'standard' : 'hq', audioCodec: 'pcm16' });
    case 'webm_vp9':
      return adaptEncode({ container: 'webm', videoCodec: 'vp9', quality: q(38, 32, 24, 15), audioCodec: 'opus', audioBitrateKbps: quality === 'low' ? 96 : 192 });
    default:
      return adaptEncode({ container: 'mp4', videoCodec: 'h264', quality: q(28, 23, 18, 10), audioCodec: 'aac', audioBitrateKbps: quality === 'low' ? 128 : 320 });
  }
}

/** Where a preset sits in the picker. */
export const PRESET_GROUPS = ['General', 'Web & social', 'Master & broadcast', 'Animation & stills', 'Audio'] as const;

export interface EncodePreset {
  id: string;
  label: string;
  group: (typeof PRESET_GROUPS)[number];
  description: string;
  encode: Partial<EncodeSettings>;
  /** Fixed frame size, or the short side (keeping the aspect ratio); neither = match the project/source. */
  width?: number;
  height?: number;
  shortSide?: number;
  /** Frame rate; absent = match. */
  fps?: number;
  /** Loudness target for exports (LUFS). */
  loudnessLufs?: number;
}

const H264_WEB = { container: 'mp4', videoCodec: 'h264', profile: 'high', rateControl: 'vbr', audioCodec: 'aac', audioSampleRate: 48000 } as const;

export const ENCODE_PRESETS: EncodePreset[] = [
  { id: 'h264_high', label: 'H.264 · High quality', group: 'General', description: 'MP4 that plays everywhere, at the project size', encode: { container: 'mp4', videoCodec: 'h264', quality: 18, audioCodec: 'aac', audioBitrateKbps: 320 } },
  { id: 'h264_draft', label: 'H.264 · Draft', group: 'General', description: 'Small, fast MP4 for checking the edit', encode: { container: 'mp4', videoCodec: 'h264', quality: 28, speed: 'fast', audioCodec: 'aac', audioBitrateKbps: 128 } },
  { id: 'h265_high', label: 'H.265 / HEVC · High quality', group: 'General', description: 'About half the size of H.264 at the same quality', encode: { container: 'mp4', videoCodec: 'h265', quality: 22, audioCodec: 'aac', audioBitrateKbps: 256 } },
  { id: 'h265_10bit', label: 'H.265 / HEVC · 10-bit', group: 'General', description: 'HEVC Main 10, smooth gradients', encode: { container: 'mp4', videoCodec: 'h265', pixelFormat: 'yuv420p10le', quality: 20, audioCodec: 'aac', audioBitrateKbps: 256 } },

  { id: 'youtube_1080', label: 'YouTube 1080p', group: 'Web & social', description: 'H.264 VBR 16 Mbps, AAC 320 kbps', shortSide: 1080, encode: { ...H264_WEB, bitrateKbps: 16000, maxBitrateKbps: 24000, audioBitrateKbps: 320 } },
  { id: 'youtube_4k', label: 'YouTube 4K', group: 'Web & social', description: 'H.264 VBR 45 Mbps, AAC 320 kbps', shortSide: 2160, encode: { ...H264_WEB, level: '5.2', bitrateKbps: 45000, maxBitrateKbps: 68000, audioBitrateKbps: 320 } },
  { id: 'vimeo_1080', label: 'Vimeo 1080p', group: 'Web & social', description: 'H.264 VBR 2-pass 20 Mbps', shortSide: 1080, encode: { ...H264_WEB, bitrateKbps: 20000, maxBitrateKbps: 30000, twoPass: true, audioBitrateKbps: 320 } },
  { id: 'reels_tiktok', label: 'Instagram Reels / TikTok (9:16)', group: 'Web & social', description: '1080×1920 H.264, 30 fps, AAC 256 kbps', width: 1080, height: 1920, fps: 30, loudnessLufs: -14, encode: { ...H264_WEB, bitrateKbps: 12000, maxBitrateKbps: 16000, keyframeInterval: 60, audioBitrateKbps: 256 } },
  { id: 'twitter_x', label: 'X / Twitter 1080p', group: 'Web & social', description: 'H.264 VBR 10 Mbps, AAC 128 kbps', shortSide: 1080, encode: { ...H264_WEB, bitrateKbps: 10000, maxBitrateKbps: 14000, audioBitrateKbps: 128 } },
  { id: 'web_small', label: 'Web · small', group: 'Web & social', description: '720p H.264 for embedding and email', shortSide: 720, encode: { container: 'mp4', videoCodec: 'h264', profile: 'main', quality: 27, audioCodec: 'aac', audioBitrateKbps: 128 } },
  { id: 'web_av1', label: 'Web · AV1 (WebM)', group: 'Web & social', description: 'Smallest files for modern browsers', encode: { container: 'webm', videoCodec: 'av1', quality: 32, audioCodec: 'opus', audioBitrateKbps: 128 } },
  { id: 'web_vp9', label: 'Web · VP9 (WebM)', group: 'Web & social', description: 'Royalty-free web video', encode: { container: 'webm', videoCodec: 'vp9', quality: 31, audioCodec: 'opus', audioBitrateKbps: 160 } },

  { id: 'prores_422_hq', label: 'Broadcast · ProRes 422 HQ', group: 'Master & broadcast', description: 'MOV, 10-bit 4:2:2, PCM 24-bit 48 kHz', encode: { container: 'mov', videoCodec: 'prores', profile: 'hq', colorSpace: 'bt709', audioCodec: 'pcm24' } },
  { id: 'prores_422', label: 'ProRes 422', group: 'Master & broadcast', description: 'MOV, 10-bit 4:2:2', encode: { container: 'mov', videoCodec: 'prores', profile: 'standard', colorSpace: 'bt709', audioCodec: 'pcm24' } },
  { id: 'prores_proxy', label: 'ProRes 422 Proxy', group: 'Master & broadcast', description: 'Light editing proxy for Final Cut and Premiere', encode: { container: 'mov', videoCodec: 'prores', profile: 'proxy', audioCodec: 'pcm16' } },
  { id: 'prores_4444', label: 'ProRes 4444', group: 'Master & broadcast', description: 'MOV, 10-bit 4:4:4 master', encode: { container: 'mov', videoCodec: 'prores', profile: '4444', colorSpace: 'bt709', audioCodec: 'pcm24' } },
  { id: 'dnxhr_hq_mxf', label: 'DNxHR HQ master (MXF)', group: 'Master & broadcast', description: 'MXF OP1a for Avid, PCM 24-bit', encode: { container: 'mxf', videoCodec: 'dnxhr', profile: 'hq', colorSpace: 'bt709', audioCodec: 'pcm24' } },
  { id: 'dnxhr_hqx_mov', label: 'DNxHR HQX (MOV)', group: 'Master & broadcast', description: '10-bit 4:2:2 DNxHR in QuickTime', encode: { container: 'mov', videoCodec: 'dnxhr', profile: 'hqx', colorSpace: 'bt709', audioCodec: 'pcm24' } },
  { id: 'dnxhr_lb', label: 'DNxHR LB (editing proxy)', group: 'Master & broadcast', description: 'Low-bandwidth DNxHR in QuickTime', encode: { container: 'mov', videoCodec: 'dnxhr', profile: 'lb', audioCodec: 'pcm16' } },
  { id: 'cineform', label: 'GoPro CineForm', group: 'Master & broadcast', description: '10-bit 4:2:2 intermediate (MOV)', encode: { container: 'mov', videoCodec: 'cineform', profile: 'film1', audioCodec: 'pcm24' } },

  { id: 'gif_preview', label: 'GIF preview', group: 'Animation & stills', description: '360p, 15 fps, looping', shortSide: 360, fps: 15, encode: { container: 'gif', videoCodec: 'gif' } },
  { id: 'webp_anim', label: 'Animated WebP', group: 'Animation & stills', description: '480p, 15 fps, much smaller than GIF', shortSide: 480, fps: 15, encode: { container: 'webp', videoCodec: 'webp', quality: 75 } },
  { id: 'png_seq', label: 'PNG sequence', group: 'Animation & stills', description: 'One lossless PNG per frame, zipped', encode: { container: 'png_seq', videoCodec: 'png' } },
  { id: 'tiff_seq', label: 'TIFF sequence (16-bit)', group: 'Animation & stills', description: 'One 16-bit TIFF per frame, zipped', encode: { container: 'tiff_seq', videoCodec: 'tiff', pixelFormat: 'rgb48' } },
  { id: 'exr_seq', label: 'OpenEXR sequence', group: 'Animation & stills', description: 'Half-float EXR frames for compositing, zipped', encode: { container: 'exr_seq', videoCodec: 'exr' } },
  { id: 'jpeg_seq', label: 'JPEG sequence', group: 'Animation & stills', description: 'One high-quality JPEG per frame, zipped', encode: { container: 'jpeg_seq', videoCodec: 'mjpeg', quality: 2 } },

  { id: 'audio_wav48', label: 'WAV 48 kHz', group: 'Audio', description: 'Uncompressed PCM 24-bit, stereo', encode: { container: 'wav', audioCodec: 'pcm24', audioSampleRate: 48000 } },
  { id: 'podcast_mp3', label: 'Podcast MP3', group: 'Audio', description: 'MP3 128 kbps 44.1 kHz, loudness -16 LUFS', loudnessLufs: -16, encode: { container: 'mp3', audioCodec: 'mp3', audioBitrateKbps: 128, audioSampleRate: 44100 } },
  { id: 'audio_aac', label: 'AAC audio (M4A)', group: 'Audio', description: 'AAC 256 kbps', encode: { container: 'm4a', audioCodec: 'aac', audioBitrateKbps: 256 } },
  { id: 'audio_flac', label: 'FLAC', group: 'Audio', description: 'Lossless, about half the size of WAV', encode: { container: 'flac', audioCodec: 'flac' } },
  { id: 'audio_aiff', label: 'AIFF 48 kHz', group: 'Audio', description: 'Uncompressed PCM 24-bit for Logic and Pro Tools', encode: { container: 'aiff', audioCodec: 'pcm24', audioSampleRate: 48000 } },
  { id: 'audio_opus', label: 'Opus', group: 'Audio', description: 'Efficient speech and music, 128 kbps', encode: { container: 'opus', audioCodec: 'opus', audioBitrateKbps: 128 } },
];

export const encodePreset = (id: string) => ENCODE_PRESETS.find((p) => p.id === id);

/** A preset's full settings. */
export const presetEncode = (p: Pick<EncodePreset, 'encode'>): EncodeSettings => normalizeEncode(p.encode);

/**
 * Frame size for a preset given the size it starts from (the project, or the
 * source of a conversion): fixed, scaled to a short side keeping the aspect
 * ratio (never upscaling for animations), or unchanged. Always even.
 */
export function presetSize(p: Pick<EncodePreset, 'width' | 'height' | 'shortSide'>, w: number, h: number): { width: number; height: number } {
  const even = (v: number) => Math.max(2, Math.round(v / 2) * 2);
  if (p.width && p.height) return { width: p.width, height: p.height };
  if (p.shortSide && w > 0 && h > 0) {
    const k = p.shortSide / Math.min(w, h);
    return { width: even(w * k), height: even(h * k) };
  }
  return { width: even(w), height: even(h) };
}

/** File type of an output. */
export function outputFileOf(e: EncodeSettings): { ext: string; mime: string } {
  const c = containerSpec(e.container);
  return c ? { ext: c.ext, mime: c.mime } : { ext: 'mp4', mime: 'video/mp4' };
}
